import { useTranslation } from 'react-i18next';
import { FormItem } from 'v5/design-system/Form';
import { Input } from 'v5/design-system/Input';
import { FieldMode, useFieldContext } from '../FieldContext';
import { fieldAnchor } from '../unitPath';
import { FieldControlBase } from './FieldControlBase';
import type { FieldFrameSpec } from './fieldComponent.types';

/**
 * After a label or a unit's title at rest, on its line and fainter (as a
 * folded list's "· 3"): the value is empty. Display mode shows values; it
 * asks for them ("Enter …") only while a field is being edited.
 */
export function EmptyValueMark() {
  const { t } = useTranslation('patterns');
  return (
    <span className="shrink-0 font-normal text-fg-muted/60 italic">
      <span aria-hidden>·</span> {t('SchemaForm.unit.noValue')}
    </span>
  );
}

/**
 * Placeholder of one part of a value (a first or last name, a currency, an
 * axis): `prompt`, except on an empty part in display mode, which stays
 * blank rather than ask for input beside the rest of the value. A filled
 * part keeps it: not shown, it still names the input (it has no label).
 */
export function partPlaceholder(
  mode: FieldMode,
  prompt: string,
  partIsEmpty: boolean
): string | undefined {
  return mode === 'display' && partIsEmpty ? undefined : prompt;
}

/**
 * A display-mode field without a value, in place of its control: its label
 * and `EmptyValueMark` on one line; with the label hidden (a list's line),
 * "no value" as the placeholder of a read-only input, in line with filled
 * rows. `path` lets a double click on the line open the unit at this field.
 */
export function EmptyField({
  spec,
  path,
  hideLabel
}: {
  spec: FieldFrameSpec;
  path: string;
  hideLabel?: boolean;
}) {
  const { t } = useTranslation('patterns');
  const ctx = useFieldContext();
  if (!hideLabel && spec.label !== '') {
    return (
      // As much room below as a value leaves, so the next label starts a field.
      <FormItem {...fieldAnchor(path)} className="pb-2">
        <p className="flex min-w-0 gap-x-2 text-base leading-none text-fg-muted">
          <span className="truncate">{spec.label}</span>
          <EmptyValueMark />
        </p>
      </FormItem>
    );
  }
  return (
    <FieldControlBase spec={spec} hideLabel={hideLabel}>
      <Input
        readOnly
        variant={ctx.variant}
        size={ctx.size}
        value=""
        placeholder={t('SchemaForm.unit.noValue')}
        className="placeholder:text-fg-muted/60 placeholder:italic"
      />
    </FieldControlBase>
  );
}
